import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import classNames from 'classnames';

import { CloseBoldIcon } from '../../icons';
import {
  getFormattedDate,
  getFormattedDisplayDate,
  isCompleteDate,
  isDateWithinBounds,
  parseFlexibleDate,
} from '../../helpers/dates';
import MaskedDateInput from '../MaskedDateInput';

import { getLabel } from './utilities/helpers';

import 'react-datepicker/dist/react-datepicker.css';

import styles from './_index.module.scss';

const DateRangePicker = ({
  'data-testid': dataTestId,
  endDate,
  isDisabled,
  label,
  maxDate,
  onDatesChange,
  startDate,
}) => {
  const [selectedEndDate, setSelectedEndDate] = useState(
    endDate ? new Date(endDate + 'T00:00:00') : null
  );
  const [selectedStartDate, setSelectedStartDate] = useState(
    startDate ? new Date(startDate + 'T00:00:00') : null
  );

  const endRef = useRef(null);
  const format = 'MM/DD/YYYY';
  const startRef = useRef(null);

  // Block react-datepicker from parsing partial input on every keystroke; only
  // a complete date may commit while typing (see onBlur for the lenient path).
  const handleChangeDate = (event) => {
    const dateString = event.target.value;
    if (!isCompleteDate(dateString)) event.preventDefault();
  };

  const handleStartBlur = (event) => {
    const nextDate = parseFlexibleDate(event.target.value);
    const committed = isDateWithinBounds({ date: nextDate, maxDate: selectedEndDate || maxDate })
      ? nextDate
      : null;
    const nextTime = committed ? committed.getTime() : null;
    const currentTime = selectedStartDate ? selectedStartDate.getTime() : null;
    if (nextTime !== currentTime) setSelectedStartDate(committed);

    return getFormattedDisplayDate(committed);
  };

  const handleEndBlur = (event) => {
    const nextDate = parseFlexibleDate(event.target.value);
    const committed = isDateWithinBounds({ date: nextDate, maxDate, minDate: selectedStartDate })
      ? nextDate
      : null;
    const nextTime = committed ? committed.getTime() : null;
    const currentTime = selectedEndDate ? selectedEndDate.getTime() : null;
    if (nextTime !== currentTime) setSelectedEndDate(committed);

    return getFormattedDisplayDate(committed);
  };

  useEffect(() => {
    if (endDate && endDate !== selectedEndDate) {
      setSelectedEndDate(endDate ? new Date(endDate + 'T00:00:00') : null);
    }

    if (startDate && startDate !== selectedStartDate) {
      setSelectedStartDate(startDate ? new Date(startDate + 'T00:00:00') : null);
    }

    if (!endDate && !startDate) {
      setSelectedEndDate(null);
      setSelectedStartDate(null);
    }
  }, [endDate, startDate]);

  useEffect(() => {
    onDatesChange([
      selectedStartDate ? getFormattedDate(selectedStartDate) : null,
      selectedEndDate ? getFormattedDate(selectedEndDate) : null,
    ]);
  }, [selectedEndDate, selectedStartDate]);

  // Once the start date commits (typed-complete, calendar pick, or blur), move
  // focus to the end input and close the start popper.
  useEffect(() => {
    if (selectedStartDate && !selectedEndDate) {
      endRef.current.setFocus();
    }

    if (selectedStartDate) {
      startRef.current.setOpen(false);
    }
  }, [selectedStartDate]);

  return (
    <div
      className={classNames(styles['date-range-picker'], { [styles['is-disabled']]: isDisabled })}
      data-testid={dataTestId}
    >
      <h4>{getLabel({ label, selectedEndDate, selectedStartDate })}</h4>
      <div>
        <DatePicker
          customInput={<MaskedDateInput onBlurInput={handleStartBlur} />}
          disabled={isDisabled}
          disabledKeyboardNavigation
          endDate={selectedEndDate}
          maxDate={selectedEndDate || new Date(maxDate + 'T00:00:00')}
          monthsShown={2}
          onChange={setSelectedStartDate}
          onChangeRaw={handleChangeDate}
          placeholderText={format}
          popperModifiers={[
            {
              name: 'flip',
              options: {
                fallbackPlacements: [],
              },
            },
          ]}
          popperPlacement="bottom-start"
          ref={startRef}
          selected={selectedStartDate}
          selectsStart
          startDate={selectedStartDate}
        />
        <DatePicker
          customInput={<MaskedDateInput onBlurInput={handleEndBlur} />}
          disabled={isDisabled}
          disabledKeyboardNavigation
          endDate={selectedEndDate}
          maxDate={new Date(maxDate + 'T00:00:00')}
          minDate={selectedStartDate}
          monthsShown={2}
          onChange={setSelectedEndDate}
          onChangeRaw={handleChangeDate}
          placeholderText={format}
          popperModifiers={[
            {
              name: 'offset',
              options: {
                offset: (/* { placement, popper, reference } */) => {
                  return [-135, 0];
                },
              },
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: [],
              },
            },
          ]}
          popperPlacement="bottom-start"
          ref={endRef}
          selected={selectedEndDate}
          selectsEnd
          startDate={selectedStartDate}
        />
        <CloseBoldIcon
          className={classNames(styles['date-range-picker-close'], {
            [styles['is-visible']]: selectedStartDate || selectedEndDate,
          })}
          onClick={() => {
            setSelectedEndDate(null);
            setSelectedStartDate(null);
          }}
        />
      </div>
    </div>
  );
};

DateRangePicker.defaultProps = {
  'data-testid': 'date-range-picker',
  endDate: null,
  maxDate: '9999-12-31',
  onDatesChange: () => {},
  startDate: null,
};

DateRangePicker.propTypes = {
  'data-testid': PropTypes.string,
  /**
   * dd-mm-yyyy format
   */
  endDate: PropTypes.string,
  isDisabled: PropTypes.bool,
  label: PropTypes.string,
  /**
   * dd-mm-yyyy format
   */
  maxDate: PropTypes.string,
  /**
   * returns [startDate, endDate]
   */
  onDatesChange: PropTypes.func,
  /**
   * dd-mm-yyyy format
   */
  startDate: PropTypes.string,
};

export default DateRangePicker;
